/**
 * Cockpit batch progress band (#113) — the per-upload roll-up pinned above
 * the Daily Cockpit's 7-day rail. One band per recent CSV upload:
 *
 *   - in flight: conic progress ring (fraction inside) + per-state counts +
 *     a rough time estimate once throughput is measurable;
 *   - finished: green band with a solid check ring, "X clear · Y need review"
 *     line, a "View batch" shortcut (shown when rows are flagged — it
 *     filters to the whole batch, not just the flagged rows), and a dismiss.
 *
 * Purely presentational — counts arrive as `BatchRollup`s (see
 * utils/batchRollup) derived from the appointment rows the Cockpit already
 * streams, so the band updates live with no extra reads.
 *
 * Colors deliberately match the status-badge palette 1:1 (green checked /
 * blue in progress / grey queued / amber can't-check) per the approved #113
 * design, so the band and the row chips below it read as one system.
 */
import type { CSSProperties } from 'react'
import type { BatchRollup } from '../utils/batchRollup'

interface BatchProgressBandProps {
  batch: BatchRollup
  /** Clinic display name, when known (admins see multi-clinic cockpits). */
  clinicName?: string
  /** Estimated ms until the batch finishes; null until throughput is measurable. */
  etaMs: number | null
  /** True when the table is currently filtered to this batch. */
  isFiltered: boolean
  onToggleFilter: () => void
  onDismiss: () => void
}

/** "9:14 AM" for today's uploads, "Wed 2:03 PM" otherwise. */
function formatUploadedAt(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  const today = new Date()
  const sameDay = d.getFullYear() === today.getFullYear()
    && d.getMonth() === today.getMonth() && d.getDate() === today.getDate()
  return sameDay ? time : `${d.toLocaleDateString('en-US', { weekday: 'short' })} ${time}`
}

function formatEta(ms: number): string {
  const min = Math.ceil(ms / 60000)
  return min <= 1 ? '~1 min' : `~${min} min`
}

// Count-dot colors, mirroring the approved mockup's segment order: answered
// (green), running (blue), can't-check (amber), queued (grey — one shade
// darker than the ring's #e2e8f0 remainder in Dashboard.css so a 7px dot
// stays legible on the white band). The ring itself paints its colors in the
// CSS conic-gradient (.batch-band__ring); these feed only the count dots.
const RING_COLORS = { answered: '#16a34a', inProgress: '#3b82f6', cantCheck: '#f59e0b', queued: '#cbd5e1' }

/**
 * The ring's conic-gradient lives in CSS; the component only feeds the three
 * cumulative stop angles as registered custom properties (@property in
 * Dashboard.css), which the browser can transition — so segment changes sweep
 * smoothly instead of repainting in a jump. Unsupporting browsers fall back
 * to the instant repaint.
 */
function ringStops(b: BatchRollup): CSSProperties {
  const pct = (n: number) => (n / b.total) * 100
  const a = pct(b.answered)
  const p = a + pct(b.inProgress)
  const c = p + pct(b.cantCheck)
  return {
    '--batch-ring-a': `${a}%`,
    '--batch-ring-p': `${p}%`,
    '--batch-ring-c': `${c}%`,
  } as CSSProperties
}

export default function BatchProgressBand({
  batch, clinicName, etaMs, isFiltered, onToggleFilter, onDismiss,
}: BatchProgressBandProps) {
  const meta = [formatUploadedAt(batch.uploadedAt), clinicName].filter(Boolean).join(' · ')
  const filterLabel = isFiltered ? 'Show all appointments' : 'Filter to this batch'

  if (batch.finished) {
    const flagged = batch.needsReview + batch.cantCheck
    const headline = batch.cantCheck === 0
      ? `All ${batch.total} checked`
      : `Finished — ${batch.answered} of ${batch.total} checked`
    const parts = [`${batch.clear} clear`]
    if (batch.needsReview > 0) parts.push(`${batch.needsReview} need${batch.needsReview === 1 ? 's' : ''} review`)
    if (batch.cantCheck > 0) parts.push(`${batch.cantCheck} can’t check`)
    return (
      <div className="batch-band batch-band--done">
        <div className="batch-band__ring-done" aria-hidden="true">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <div className="batch-band__mid">
          <div className="batch-band__headline">{headline} — {batch.fileName}</div>
          <div className="batch-band__meta">
            {parts.join(' · ')}{meta ? ` · finished after upload ${meta}` : ''}
          </div>
        </div>
        {flagged > 0 && (
          <button type="button" className="batch-band__btn" onClick={onToggleFilter}>
            {/* The filter shows the whole batch (flagged rows among it), so
                the label must not promise a flagged-only view. */}
            {isFiltered ? 'Show all appointments' : 'View batch'}
          </button>
        )}
        <button type="button" className="batch-band__dismiss" onClick={onDismiss}>
          Dismiss ✕
        </button>
      </div>
    )
  }

  return (
    <div className="batch-band">
      <div className="batch-band__ring" style={ringStops(batch)} aria-hidden="true">
        <div className="batch-band__ring-inner">
          <span className="batch-band__ring-n">{batch.answered}/{batch.total}</span>
          <span className="batch-band__ring-of">checked</span>
        </div>
      </div>
      <div className="batch-band__mid">
        <div className="batch-band__file">
          <span className="batch-band__live-dot" aria-hidden="true" />
          {batch.fileName}
          {meta && <small>uploaded {meta}</small>}
        </div>
        {/* Zero-count states are noise ("0 in progress" while a batch sits
            queued reads broken) — list only what's actually happening. The
            headline "checked" count always shows: 0/16 at start is the story. */}
        <div className="batch-band__counts">
          <span><span className="batch-band__dot" style={{ background: RING_COLORS.answered }} /><b>{batch.answered}</b> checked</span>
          {batch.inProgress > 0 && (
            <span><span className="batch-band__dot" style={{ background: RING_COLORS.inProgress }} /><b>{batch.inProgress}</b> in progress</span>
          )}
          {batch.queued > 0 && (
            <span><span className="batch-band__dot" style={{ background: RING_COLORS.queued }} /><b>{batch.queued}</b> queued</span>
          )}
          {batch.cantCheck > 0 && (
            <span><span className="batch-band__dot" style={{ background: RING_COLORS.cantCheck }} /><b>{batch.cantCheck}</b> can’t check</span>
          )}
        </div>
      </div>
      {etaMs !== null && (
        <div className="batch-band__eta">
          <div className="batch-band__eta-label">est. remaining</div>
          <div className="batch-band__eta-value">{formatEta(etaMs)}</div>
        </div>
      )}
      <button type="button" className="batch-band__btn" onClick={onToggleFilter}>
        {filterLabel}
      </button>
    </div>
  )
}
